import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { BrowserCodeReader, BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import type { IScannerControls } from '@zxing/browser'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog'
import { isMobileDevice } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'

const SCAN_TIMEOUT_MS = 500
const BARCODE_MAX_LENGTH = 100
const BARCODE_MIN_LENGTH = 1
const BARCODE_PATTERN = /^[A-Za-z0-9\-_.:/\\]+$/
const RESCAN_SAME_BARCODE_COOLDOWN_MS = 3000

const ENABLED_FORMATS = [
  BarcodeFormat.CODE_128,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.EAN_13,
  BarcodeFormat.QR_CODE,
]

const FORMAT_HINTS: Map<DecodeHintType, unknown> = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, ENABLED_FORMATS],
])

interface BarcodeScannerProps {
  onScan: (value: string) => void
  trigger: ReactNode
}

const isValidBarcode = (value: string): boolean =>
  value.length >= BARCODE_MIN_LENGTH &&
  value.length <= BARCODE_MAX_LENGTH &&
  BARCODE_PATTERN.test(value)

const BarcodeScanner = ({ onScan, trigger }: BarcodeScannerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [scanBuffer, setScanBuffer] = useState('')
  const [cameraAvailable, setCameraAvailable] = useState(false)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null | undefined>(undefined)

  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerControlsRef = useRef<IScannerControls | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scanBufferRef = useRef('')
  const hasEmittedRef = useRef(false)
  const lastEmittedValueRef = useRef<string | null>(null)
  const lastEmittedTimeRef = useRef<number>(0)

  const stopCamera = useCallback(() => {
    scannerControlsRef.current?.stop()
    scannerControlsRef.current = null
    BrowserCodeReader.releaseAllStreams()
  }, [])

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }, [])

  const resetScannerState = useCallback(() => {
    stopCamera()
    clearDebounceTimer()
    scanBufferRef.current = ''
    setScanBuffer('')
    setCameraAvailable(false)
    setSelectedDeviceId(undefined)
  }, [stopCamera, clearDebounceTimer])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        hasEmittedRef.current = false
      } else {
        resetScannerState()
      }
      setIsOpen(open)
    },
    [resetScannerState],
  )

  const closeModal = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  const emitScan = useCallback(
    (value: string) => {
      if (hasEmittedRef.current) return
      if (!isValidBarcode(value)) {
        scanBufferRef.current = ''
        setScanBuffer('')
        return
      }
      const timeSinceLastScan = Date.now() - lastEmittedTimeRef.current
      if (
        value === lastEmittedValueRef.current &&
        timeSinceLastScan < RESCAN_SAME_BARCODE_COOLDOWN_MS
      ) {
        return
      }
      hasEmittedRef.current = true
      lastEmittedValueRef.current = value
      lastEmittedTimeRef.current = Date.now()
      onScan(value)
      closeModal()
    },
    [onScan, closeModal],
  )

  const handleKeyDown = useCallback(() => {
    clearDebounceTimer()
  }, [clearDebounceTimer])

  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const currentBuffer = scanBufferRef.current

      if (event.key === 'Enter') {
        if (currentBuffer.length > 0) {
          clearDebounceTimer()
          emitScan(currentBuffer)
          scanBufferRef.current = ''
          setScanBuffer('')
        }
        return
      }

      if (event.key.length !== 1) return

      const updatedBuffer = currentBuffer + event.key
      scanBufferRef.current = updatedBuffer
      setScanBuffer(updatedBuffer)

      debounceTimerRef.current = setTimeout(() => {
        emitScan(updatedBuffer)
        scanBufferRef.current = ''
        setScanBuffer('')
      }, SCAN_TIMEOUT_MS)
    },
    [clearDebounceTimer, emitScan],
  )

  const handleModalClick = useCallback(() => {
    hiddenInputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!isOpen || !isMobileDevice()) return

    let cancelled = false

    BrowserMultiFormatReader.listVideoInputDevices()
      .then((videoInputDevices) => {
        if (cancelled || videoInputDevices.length === 0) {
          setCameraAvailable(false)
          return
        }

        const rearCamera = videoInputDevices.find((device) =>
          /back|rear|environment/i.test(device.label),
        )
        const chosenDevice = rearCamera ?? videoInputDevices[0]

        setSelectedDeviceId(chosenDevice.deviceId || null)
        setCameraAvailable(true)
      })
      .catch(() => {
        if (!cancelled) setCameraAvailable(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !cameraAvailable || selectedDeviceId === undefined || !videoRef.current) return

    let cancelled = false
    const reader = new BrowserMultiFormatReader(FORMAT_HINTS)
    const videoElement = videoRef.current

    reader
      .decodeFromVideoDevice(selectedDeviceId ?? undefined, videoElement, (result, error) => {
        if (result) {
          emitScan(result.getText())
        }
        void error
      })
      .then((scannerControls) => {
        if (!cancelled) {
          scannerControlsRef.current = scannerControls
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCameraAvailable(false)
        }
      })

    return () => {
      cancelled = true
      stopCamera()
    }
  }, [isOpen, cameraAvailable, selectedDeviceId, emitScan, stopCamera])

  useEffect(() => {
    return () => {
      stopCamera()
      clearDebounceTimer()
    }
  }, [stopCamera, clearDebounceTimer])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => hiddenInputRef.current?.focus(), 50)
    }
  }, [isOpen])

  const showCameraStream = isMobileDevice() && cameraAvailable
  const scanningDescription =
    scanBuffer.length > 0
      ? t('barcodeScanner.scanning', { count: scanBuffer.length })
      : t('barcodeScanner.scanNow')

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent onClick={handleModalClick}>
        <DialogTitle className="sr-only">{t('barcodeScanner.title')}</DialogTitle>
        <input
          ref={hiddenInputRef}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          aria-hidden="true"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          readOnly
        />

        {showCameraStream ? (
          <div className="flex flex-col items-center gap-4">
            <DialogDescription className="text-center">
              {t('barcodeScanner.pointCamera')}
            </DialogDescription>
            <video ref={videoRef} className="w-full rounded-md" autoPlay playsInline muted />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-8">
            <DialogDescription className="text-center">
              {isMobileDevice() && !cameraAvailable
                ? t('barcodeScanner.noCamera')
                : scanningDescription}
            </DialogDescription>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BarcodeScanner
